import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '../UI/Modal';

interface LoginFormProps {
  onSubmit: (email: string, password: string) => Promise<boolean>;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSubmit }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await onSubmit(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ocorreu um erro ao fazer login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowForgotPasswordModal(true);
  };

  return (
    <div className="login-container">
      <form onSubmit={handleSubmit} className="w-full">
        <div className="form-group">
          <label htmlFor="email" className="form-label">E-mail</label>
          <input
            type="email"
            id="email"
            className="form-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="seu@email.com"
            disabled={isLoading}
            autoComplete="off"
          />
        </div>

        <div className="form-group">
          <label htmlFor="password" className="form-label">Senha</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              className="form-input pr-10"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              disabled={isLoading}
              maxLength={20}
              autoComplete="off"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              disabled={isLoading}
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-4.477-10-10 0-1.657.403-3.221 1.125-4.575M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {error && <div className="text-red-600 text-sm mb-4">{error}</div>}

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="remember"
              className="h-4 w-4 text-blue-600"
              disabled={isLoading}
            />
            <label htmlFor="remember" className="ml-2 text-sm text-gray-700">
              Lembrar-me
            </label>
          </div>
          <button
            type="button"
            onClick={handleForgotPasswordClick}
            className="text-sm text-blue-600 hover:underline focus:outline-none"
            disabled={isLoading}
          >
            Esqueceu sua senha?
          </button>
        </div>

        <button
          type="submit"
          className="btn btn-primary"
          disabled={isLoading}
        >
          {isLoading ? 'Entrando...' : 'Entrar'}
        </button>

        <div className="social-login" style={{ display: 'none' }}>
          <button
            type="button"
            className="btn social-btn"
            disabled={isLoading}
          >
            <img
              src="/google-icon.svg"
              alt=""
              className="w-5 h-5"
              aria-hidden="true"
            />
            <span>Entrar com Google</span>
          </button>
          <button
            type="button"
            className="btn social-btn"
            disabled={isLoading}
          >
            <img
              src="/microsoft-icon.svg"
              alt=""
              className="w-5 h-5"
              aria-hidden="true"
            />
            <span>Entrar com Microsoft</span>
          </button>
        </div>
      </form>

      <Modal
        isOpen={showForgotPasswordModal}
        title="Recuperação de Senha"
        onClose={() => setShowForgotPasswordModal(false)}
      >
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
            <svg
              className="h-6 w-6 text-yellow-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Recurso Desabilitado
          </h3>
          <p className="text-sm text-gray-500 mb-6">
            A recuperação de senha está temporariamente desabilitada no sistema.
            <br />
            <br />
            Para alterar sua senha, entre em contato com o administrador do sistema.
          </p>
          <button
            onClick={() => setShowForgotPasswordModal(false)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Entendi
          </button>
        </div>
      </Modal>
    </div>
  );
}; 